import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from 'react-router-dom';
import { Fab, Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';

import OverLayLoader from '../../../components/OverLayLoader';
import DictionaryFilter from '../../../components/DictionaryFilter';
import FunctionalTable from '../../../components/FunctionalTable';
import NowrapWrapper from '../../../components/NowrapWrapper';
import { EditableCell } from '../../../components/EditableCell';
import EditableHighlightedCell from '../../../components/EditableHighlightedCell';
import EditableSelectCell from '../../../components/EditableSelectCell';
import FloatingActionButtonHolder from '../../../components/FloatingActionButtonHolder';
import { SelectColumnFilter } from '../../../components/ColumnFilter';

import {
  getLanguages,
  getReplacements,
  updateReplacement,
  deleteReplacement,
  getReplacementTypes,
  downloadReplacementsCode,
} from '../../../services';
import useTitle from '../../../hooks/useTitle';
import usePersistColumns from '../../../hooks/usePersistColumns';
import useDeleteRow from '../../../hooks/useDeleteRow';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DeleteRowButton from '../../../components/DeleteRowButton';
import {
  languageOption,
  optionalReplacementsColumns,
} from '../../../helpers/options';
import { REPLACEMENTS_KEYS } from '../../../helpers/tableKeys';
import saveFile from '../../../utils/saveFile';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import PATH from '../../../helpers/routes.json';
import { useAuth } from '../../../context/auth/AuthContext';

const initialFilter = {
  language: null,
  additions: optionalReplacementsColumns,
};

const {
  COMMENT,
  REPLACED,
  REPLACEMENT,
  REPLACEMENT_TYPE,
  OFFSET,
  LIMIT,
  LANGUAGE_ID,
} = REPLACEMENTS_KEYS;

const ReplacementsView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [languageList, setLanguageList] = useState(null);
  const [typesList, setTypesList] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [isSearchParamsLoading, setIsSearchParamsLoading] = useState(true);
  const [filter, setFilter] = useState(initialFilter);
  const [fetchKey, setFetchKey] = useState(0);

  const {
    isDeleting,
    itemToDelete: replacementToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  } = useDeleteRow({
    onDelete: item => deleteReplacement(item.id),
    onSuccess: () => setFetchKey(prev => prev + 1),
  });

  const activeFetchRequestsRef = useRef(0);

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { t } = useTranslation(['replacements', 'promptbar', 'common']);

  const isBlockingBusy = isSaving || isSearchParamsLoading;
  const isPageBusy = isFetching || isBlockingBusy;
  const shouldShowOverlay = isOptionsLoading || isBlockingBusy || isFetching;

  const title = t('common:pages.dictionaries.tabs.replacements');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  usePersistColumns('replacementsColumns', ['language', 'additions']);

  useEffect(() => {
    if (isOptionsLoading) {
      return;
    }

    const savedLanguage = searchParams.get('language');
    if (
      savedLanguage &&
      languageList?.some(language => language.name === savedLanguage)
    ) {
      setFilter(prev => ({ ...prev, language: savedLanguage }));
    }

    if (
      searchParams.has('additions') &&
      !searchParams.get('additions')?.length
    ) {
      setFilter(prev => ({ ...prev, additions: [] }));
    } else if (
      searchParams.has('additions') &&
      searchParams.get('additions')?.length
    ) {
      const additions = searchParams.get('additions').split(',');
      setFilter(prev => ({ ...prev, additions }));
    } else {
      searchParams.set('additions', initialFilter.additions.join(','));
      setFilter(prev => ({ ...prev, additions: initialFilter.additions }));
    }

    setSearchParams(searchParams, { replace: true });
    setIsSearchParamsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchFilterOptions = async () => {
      try {
        const [languages, types] = await Promise.all([
          getLanguages(),
          getReplacementTypes(),
        ]);
        const defaultLang =
          languages.data.find(el => el.id === user.data_language_id)?.name ||
          languageOption[0];
        const savedLanguage = new URLSearchParams(window.location.search).get(
          'language',
        );
        const selectedLanguage = languages.data.some(
          language => language.name === savedLanguage,
        )
          ? savedLanguage
          : defaultLang;

        setLanguageList(languages.data);
        setTypesList(['all', ...types.data]);
        setFilter(prev => ({ ...prev, language: selectedLanguage }));
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsOptionsLoading(false);
      }
    };
    fetchFilterOptions();
  }, [user]);

  const fetchData = useCallback(
    async ({ params, signal }) => {
      if (!languageList) {
        return;
      }

      const currentLanguageID = languageList.find(
        lang => lang.name === params.globalFilter,
      )?.id;

      if (!currentLanguageID) {
        return;
      }

      activeFetchRequestsRef.current += 1;
      setIsFetching(true);

      const newParams = {
        [OFFSET]: params[OFFSET],
        [LIMIT]: params[LIMIT],
        [LANGUAGE_ID]: currentLanguageID,
        ...(params[REPLACED]
          ? {
              [REPLACED]: params[REPLACED],
            }
          : {}),
        ...(params[REPLACEMENT]
          ? {
              [REPLACEMENT]: params[REPLACEMENT],
            }
          : {}),
        ...(params[COMMENT]
          ? {
              [COMMENT]: params[COMMENT],
            }
          : {}),
        ...(params[REPLACEMENT_TYPE] && params[REPLACEMENT_TYPE] !== 'all'
          ? {
              [REPLACEMENT_TYPE]: params[REPLACEMENT_TYPE],
            }
          : {}),
      };

      try {
        const data = await getReplacements(newParams, signal);
        if (data) {
          const newData = data.data.map(el => ({ ...el, uniqId: el.id }));
          setData(prev =>
            params[OFFSET] === 0 ? [...newData] : [...prev, ...newData],
          );
          setHasMoreData(prev => (params[OFFSET] === 0 ? true : prev));
          if (data.data.length < params[LIMIT]) {
            setHasMoreData(false);
          }
        }
      } catch (error) {
        createErrorNotification();
        console.log(error.message);
      } finally {
        activeFetchRequestsRef.current = Math.max(
          0,
          activeFetchRequestsRef.current - 1,
        );

        if (activeFetchRequestsRef.current === 0) {
          setIsFetching(false);
        }
      }
    },
    [languageList],
  );

  // фильтр
  const handleFilterSelect = event => {
    const { value, name } = event.target;
    setFilter(prev => ({ ...prev, [name]: value }));

    searchParams.set(name, value);
    setSearchParams(searchParams);
  };

  const handleFilterReset = () => {
    navigate(location.pathname, { replace: true });
    setTimeout(() => navigate(0));
  };

  // редактирование строк таблицы
  const editHandler = codeId => {
    setData(prev =>
      prev.map(el =>
        el.id === codeId ? { ...el, isEdit: true, prevState: el } : el,
      ),
    );
  };

  const handleChangeRowData = ({ index, value, columnName, isValid }) => {
    switch (columnName) {
      case REPLACED: {
        setData(prev =>
          prev.map(row => {
            if (index === row.id) {
              return {
                ...row,
                [REPLACED]: value,
                isValid: row.isValid
                  ? { ...row.isValid, [columnName]: isValid }
                  : { [columnName]: isValid },
              };
            } else {
              return row;
            }
          }),
        );
        break;
      }

      case REPLACEMENT: {
        setData(prev =>
          prev.map(row => {
            if (index === row.id) {
              return {
                ...row,
                [REPLACEMENT]: value,
                isValid: row.isValid
                  ? { ...row.isValid, [columnName]: isValid }
                  : { [columnName]: isValid },
              };
            } else {
              return row;
            }
          }),
        );
        break;
      }

      case REPLACEMENT_TYPE: {
        setData(prev =>
          prev.map(row => {
            if (index === row.id) {
              return { ...row, [REPLACEMENT_TYPE]: value };
            } else {
              return row;
            }
          }),
        );
        break;
      }

      case COMMENT: {
        setData(prev =>
          prev.map(row => {
            if (index === row.id) {
              return { ...row, [COMMENT]: value };
            } else {
              return row;
            }
          }),
        );
        break;
      }

      default:
        return;
    }
  };

  const cancelChangesHandler = replacementId => {
    setData(prev =>
      prev.map(el => (el.id === replacementId ? { ...el.prevState } : el)),
    );
  };

  const applyChangesHandler = async (replacementId, data) => {
    if (data.isValid && typeof data.isValid === 'object') {
      const isDataValid = Object.values(data.isValid).every(el => el);
      if (!isDataValid) return;
    }

    try {
      setIsSaving(true);
      const params = {
        replaced: data.replaced,
        replacement: data.replacement,
        replacement_type: data.replacement_type,
        comment: data.comment,
      };
      const result = await updateReplacement(replacementId, params);
      if (result) {
        setFetchKey(prev => prev + 1);
      }
    } catch (error) {
      createErrorNotification();
      console.log(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const deleteActionComponent = useCallback(
    row => {
      if (row.isEdit) {
        return null;
      }
      return (
        <DeleteRowButton
          disabled={isPageBusy || isDeleting}
          onClick={() =>
            openDeleteDialog({ id: row.id, replaced: row.replaced })
          }
        />
      );
    },
    [isDeleting, isPageBusy, openDeleteDialog],
  );

  const handleDownloadExcel = async () => {
    try {
      setIsSaving(true);

      const currentLang = languageList.find(
        lang => filter.language === lang.name,
      );
      const params = {
        [LANGUAGE_ID]: currentLang?.id || languageOption[0].id,
      };

      const result = await downloadReplacementsCode(params);

      if (result) {
        const blob = new Blob([result.data], {
          type: 'application/octet-stream',
        });
        saveFile(blob, 'replacements.xlsx');
      }
    } catch (error) {
      createErrorNotification();
      console.log(error);
    } finally {
      setIsSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t(`table.replaced`)} />,
        accessor: REPLACED,
        id: REPLACED,
        className: 'text-left',
        style: { width: '28%', maxWidth: '250px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value
              ) : (
                <EditableHighlightedCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeRowData}
                  lang={filter.language}
                  replacementType={cell.row.original[REPLACEMENT_TYPE]}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.replacement`)} />,
        accessor: REPLACEMENT,
        id: REPLACEMENT,
        className: 'text-left',
        style: { width: '28%', maxWidth: '250px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value
              ) : (
                <EditableHighlightedCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeRowData}
                  lang={filter.language}
                />
              )}
            </Box>
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.replacement_type`)} />,
        accessor: REPLACEMENT_TYPE,
        id: REPLACEMENT_TYPE,
        show: filter.additions.includes(REPLACEMENT_TYPE),
        style: { width: '13%', maxWidth: '250px' },
        Filter: cell => <SelectColumnFilter cell={cell} options={typesList} />,
        Cell: ({ value: initialValue, row: { index, original } }) => {
          const editableTypes = typesList.filter(type => type !== 'all');

          return (
            <EditableSelectCell
              value={initialValue}
              isEdit={original.isEdit}
              rowIndex={index}
              rowUniqId={original.uniqId}
              columnName={REPLACEMENT_TYPE}
              options={editableTypes}
              onChangeData={handleChangeRowData}
            />
          );
        },
      },
      {
        Header: <NowrapWrapper title={t(`table.comment`)} />,
        accessor: COMMENT,
        id: COMMENT,
        show: filter.additions.includes(COMMENT),
        className: 'text-left',
        style: { width: '29%', maxWidth: '250px' },
        Cell: ({ value, ...cell }) => {
          const isEdit = cell.row.original.isEdit;

          return (
            <Box
              sx={{
                width: '100%',
              }}
            >
              {!isEdit ? (
                value
              ) : (
                <EditableCell
                  value={value}
                  cell={cell}
                  onChangeData={handleChangeRowData}
                  pathname={location.pathname}
                />
              )}
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, t, location.pathname, typesList, deleteActionComponent],
  );

  return (
    <>
      {shouldShowOverlay && <OverLayLoader />}
      {!isOptionsLoading && (
        <Box>
          <DictionaryFilter
            multipleLanguage={false}
            filter={filter}
            onFilterReset={handleFilterReset}
            options={optionalReplacementsColumns}
            onFilterSelect={handleFilterSelect}
            onDownload={handleDownloadExcel}
          />

          {!isSearchParamsLoading && (
            <FunctionalTable
              key={fetchKey}
              type="sticky"
              columns={columns}
              data={data}
              onEdit={editHandler}
              fetchData={fetchData}
              applyChanges={applyChangesHandler}
              cancelChanges={cancelChangesHandler}
              hasMoreData={hasMoreData}
              isInfiniteScroll
              pageSize={50}
              globalFilter={filter.language}
              actionColumnComponent={deleteActionComponent}
            />
          )}

          <FloatingActionButtonHolder>
            <Tooltip title={t('promptbar:addReplacement')} arrow>
              <Link
                to={PATH.dictionaries.addReplacement + '/' + filter.language}
                state={location}
              >
                <Fab color="primary" aria-label="add code">
                  <AddIcon />
                </Fab>
              </Link>
            </Tooltip>
          </FloatingActionButtonHolder>
        </Box>
      )}

      <DeleteConfirmDialog
        open={Boolean(replacementToDelete)}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={t('replacements:deleteDialog.title')}
        description={t('replacements:deleteDialog.description', {
          replaced: replacementToDelete?.replaced || '-',
        })}
      />
    </>
  );
};

export default ReplacementsView;
